'use client';
import { useState, useEffect } from 'react';
import { useDesign } from '@/context/DesignContext';

interface NavbarProps {
  logo?: string;
  logoUrl?: string;
  links?: string[] | string;
  design?: any;
}

export default function Navbar({ logo, logoUrl, links, design: propDesign }: NavbarProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  const linksArray = Array.isArray(links) ? links : [];
  const [navOpaque, setNavOpaque] = useState(false);

  useEffect(() => {
    const handleScroll = () => setNavOpaque(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Design tokens with fallbacks
  const bgOpaque = design?.colors?.secondary || '#0F0F0F';
  const borderColor = design?.colors?.primary || '#C8A96E';
  const logoColor = design?.colors?.primary || '#C8A96E';
  const linkColor = design?.colors?.textLight || '#A3A3A3';
  const linkHoverColor = design?.colors?.primary || '#C8A96E';
  const iconColor = design?.colors?.textLight || '#A3A3A3';
  const iconHoverColor = design?.colors?.primary || '#C8A96E';
  const headingFont = design?.typography?.headingFont || "'Cormorant Garamond', serif";
  const bgColor = navOpaque ? bgOpaque : 'transparent';
  const border = navOpaque ? `1px solid ${borderColor}26` : 'none';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 32px',
        background: bgColor,
        backdropFilter: navOpaque ? 'blur(20px)' : 'none',
        borderBottom: border,
        transition: 'all 0.5s',
        fontFamily: 'inherit',
      }}
    >
      {/* Left: Logo / Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {logoUrl ? (
          <img src={logoUrl} alt={logo || 'Logo'} style={{ height: '32px', width: 'auto' }} />
        ) : logo ? (
          <span style={{ fontWeight: 'bold' }}>{logo}</span>
        ) : null}
        <span
          style={{
            fontFamily: headingFont,
            fontSize: '1.5rem',
            letterSpacing: '0.3em',
            fontWeight: '300',
            color: logoColor,
          }}
        >
          {logo}
        </span>
      </div>

      {/* Center: Links */}
      {linksArray.length > 0 && (
        <div style={{ display: 'none', gap: '40px', alignItems: 'center' }} className="md:flex">
          {linksArray.map((item) => (
            <a
              key={item}
              href="#"
              style={{
                color: linkColor,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: '300',
                transition: 'color 0.3s',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
              onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
            >
              {item}
            </a>
          ))}
        </div>
      )}

      {/* Right: Icons and mobile button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <button
          style={{ color: iconColor, transition: 'color 0.3s', background: 'none', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = iconHoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = iconColor)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </button>
        <button
          style={{ color: iconColor, transition: 'color 0.3s', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = iconHoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = iconColor)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              background: design?.colors?.primary || '#C8A96E',
              color: design?.colors?.secondary || '#0F0F0F',
              fontSize: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            2
          </span>
        </button>
        <button
          style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'none', border: 'none', cursor: 'pointer' }}
          className="md:hidden"
        >
          <span style={{ display: 'block', width: '24px', height: '1px', background: iconColor, transition: 'all 0.3s' }} />
          <span style={{ display: 'block', width: '24px', height: '1px', background: iconColor, transition: 'all 0.3s' }} />
          <span style={{ display: 'block', width: '24px', height: '1px', background: iconColor, transition: 'all 0.3s' }} />
        </button>
      </div>
    </div>
  );
}